import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute, { isPlatformUser, isTenantUser } from "@/components/ProtectedRoute";
import RouteSplash from "@/components/RouteSplash";
import ForcePasswordChangeModal from "@/components/ForcePasswordChangeModal";
import RequirePermission from "@/components/RequirePermission";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Landing is imported eagerly — it's the entry point for anonymous
// visitors and RootRedirect renders it synchronously below.
import Landing from "@/pages/Landing";

// Everything else is code-split. Auth/public utility pages are small but
// still lazy so the initial bundle only ships what "/" actually needs;
// the dashboard and superadmin trees (~30 pages) are the real payoff —
// previously all shipped in the main bundle regardless of role.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const InviteAccept = lazy(() => import("@/pages/InviteAccept"));
const MagicLink = lazy(() => import("@/pages/MagicLink"));
const PublicTenant = lazy(() => import("@/pages/PublicTenant"));
const PublicOrderReceipt = lazy(() => import("@/pages/PublicOrderReceipt"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PublicBooking = lazy(() => import("@/pages/PublicBooking"));
const PublicLegal = lazy(() => import("@/pages/PublicLegal"));
const PlatformLegal = lazy(() => import("@/pages/PlatformLegal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const CheckYourEmail = lazy(() => import("@/pages/CheckYourEmail"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));

const DashboardShell = lazy(() => import("@/pages/dashboard/DashboardShell"));
const Overview = lazy(() => import("@/pages/dashboard/Overview"));
const NotificationsPage = lazy(() => import("@/pages/dashboard/Notifications"));
const Analytics = lazy(() => import("@/pages/dashboard/Analytics"));
const Modules = lazy(() => import("@/pages/dashboard/Modules"));
const Playbook = lazy(() => import("@/pages/dashboard/Playbook"));
const WhatsApp = lazy(() => import("@/pages/dashboard/WhatsApp"));
const PublicPageEditor = lazy(() => import("@/pages/dashboard/PublicPageEditor"));
const Team = lazy(() => import("@/pages/dashboard/Team"));
const Settings = lazy(() => import("@/pages/dashboard/Settings"));
const VerticalTemplates = lazy(() => import("@/pages/dashboard/VerticalTemplates"));
const SetupWizard = lazy(() => import("@/pages/dashboard/SetupWizard"));
const AuditLog = lazy(() => import("@/pages/dashboard/AuditLog"));
const ModuleLocked = lazy(() => import("@/pages/dashboard/ModuleLocked"));
const Bookings = lazy(() => import("@/pages/dashboard/Bookings"));
const KioskBookings = lazy(() => import("@/pages/dashboard/KioskBookings"));
const Resources = lazy(() => import("@/pages/dashboard/Resources"));
const Services = lazy(() => import("@/pages/dashboard/Services"));
const Catalog = lazy(() => import("@/pages/dashboard/Catalog"));
const Orders = lazy(() => import("@/pages/dashboard/Orders"));
const OrderDetail = lazy(() => import("@/pages/dashboard/OrderDetail"));
const Kitchen = lazy(() => import("@/pages/dashboard/Kitchen"));
const Accounting = lazy(() => import("@/pages/dashboard/Accounting"));
const HelpForwarding = lazy(() => import("@/pages/dashboard/HelpForwarding"));
const Simulator = lazy(() => import("@/pages/dashboard/Simulator"));
const Billing = lazy(() => import("@/pages/dashboard/Billing"));
const Legal = lazy(() => import("@/pages/dashboard/Legal"));
const ClientsBlacklist = lazy(() => import("@/pages/dashboard/ClientsBlacklist"));
const Conversations = lazy(() => import("@/pages/dashboard/Conversations"));

const SuperAdminLayout = lazy(() => import("@/pages/superadmin/SuperAdminLayout"));
const AdminOverview = lazy(() => import("@/pages/superadmin/AdminOverview"));
const AdminTenants = lazy(() => import("@/pages/superadmin/AdminTenants"));
const AdminTenantDetail = lazy(() => import("@/pages/superadmin/AdminTenantDetail"));
const AdminTenantBilling = lazy(() => import("@/pages/superadmin/AdminTenantBilling"));
const AdminHealth = lazy(() => import("@/pages/superadmin/AdminHealth"));
const AdminVerticalConfig = lazy(() => import("@/pages/superadmin/AdminVerticalConfig"));
const AdminVerticalDetail = lazy(() => import("@/pages/superadmin/AdminVerticalDetail"));
const AdminSettings = lazy(() => import("@/pages/superadmin/AdminSettings"));
const AdminRoles = lazy(() => import("@/pages/superadmin/AdminRoles"));
const AdminLeads = lazy(() => import("@/pages/superadmin/AdminLeads"));
const AdminAnalytics = lazy(() => import("@/pages/superadmin/AdminAnalytics"));
const AdminBilling = lazy(() => import("@/pages/superadmin/AdminBilling"));
const AdminPricing = lazy(() => import("@/pages/superadmin/AdminPricing"));
const AdminLegal = lazy(() => import("@/pages/superadmin/AdminLegal"));
const AdminFlowEditorV2 = lazy(() => import("@/pages/superadmin/AdminFlowEditorV2"));
const AdminCommerceConfig = lazy(() => import("@/pages/superadmin/AdminCommerceConfig"));
const AdminLatencyDashboards = lazy(() => import("@/pages/superadmin/AdminLatencyDashboards"));
const AdminTrialSettings = lazy(() => import("@/pages/superadmin/AdminTrialSettings"));
const AdminUnderstandingQuality = lazy(() => import("@/pages/superadmin/AdminUnderstandingQuality"));
const AdminVoiceObservability = lazy(() => import("@/pages/superadmin/AdminVoiceObservability"));
const AdminKnowledgeBase = lazy(() => import("@/pages/superadmin/AdminKnowledgeBase"));
const AdminLearningLab = lazy(() => import("@/pages/superadmin/AdminLearningLab"));

import "@/App.css";

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Landing />;
  const perms = user.permissions || [];
  const isPlatformUser = user.role === "SUPER_ADMIN" || perms.some((p) => p.startsWith("platform."));
  if (isPlatformUser) return <Navigate to="/admin" replace />;
  if (user.tenant_id) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" />
            <ImpersonationBanner />
            <RouteSplash />
            <ForcePasswordChangeModal />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/check-your-email" element={<CheckYourEmail />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/invite/accept" element={<InviteAccept />} />
              <Route path="/magic-link" element={<MagicLink />} />
              <Route path="/p/:slug" element={<PublicTenant />} />
              <Route path="/p/order/:orderId/paid" element={<PublicOrderReceipt />} />
              <Route path="/p/order/:orderId/cancelled" element={<PublicOrderReceipt />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/p/:slug/pricing" element={<Pricing />} />
              <Route path="/p/:slug/legal/:docType" element={<PublicLegal />} />
              <Route path="/legal/:docType" element={<PlatformLegal />} />
              <Route path="/p/:slug/book" element={<PublicBooking />} />
              <Route path="/p/:slug/booking/:token" element={<PublicBooking />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allow={isTenantUser}>
                    <DashboardShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Overview />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="modules" element={<Modules />} />
                <Route path="playbook" element={
                  <RequirePermission perm="coach.write"><Playbook /></RequirePermission>
                } />
                <Route path="public-page" element={<PublicPageEditor />} />
                <Route path="setup" element={<VerticalTemplates />} />
                <Route path="quickstart" element={<SetupWizard />} />
                <Route path="team" element={<Team />} />
                <Route path="settings" element={<Settings />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="conversations" element={<Conversations />} />
                <Route path="whatsapp" element={<WhatsApp />} />
                {/* 2026-09-25 (Itachi) — Alertes IA / Rappels à faire folded
                    into Conversations as two filter toggles (fewer nav
                    pages showing the same underlying data). Old links/
                    bookmarks still land on the right view. */}
                <Route path="callbacks" element={<Navigate to="/dashboard/conversations?queue=callbacks" replace />} />
                <Route path="alerts" element={<Navigate to="/dashboard/conversations?queue=alerts" replace />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="bookings/kiosk" element={<KioskBookings />} />
                <Route path="resources" element={<Resources />} />
                <Route path="services" element={<Services />} />
                <Route path="clients/blacklist" element={<ClientsBlacklist />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/:id" element={<OrderDetail />} />
                <Route path="kitchen" element={<Kitchen />} />
                <Route path="catalog" element={<Catalog />} />
                <Route path="accounting" element={<Accounting />} />
                <Route path="help/forwarding" element={<HelpForwarding />} />
                <Route path="billing" element={
                  <RequirePermission perm="usage.read"><Billing /></RequirePermission>
                } />
                <Route path="legal" element={
                  <RequirePermission perm="tenant.read"><Legal /></RequirePermission>
                } />
                <Route path="simulator" element={
                  <RequirePermission perm="simulator.use"><Simulator /></RequirePermission>
                } />
                <Route path="clients" element={<ModuleLocked moduleKey="M7" name="Clients" />} />
                <Route path="analytics" element={<Analytics />} />
              </Route>

              <Route
                path="/admin"
                element={
                  <ProtectedRoute allow={isPlatformUser}>
                    <SuperAdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminOverview />} />
                <Route path="tenants" element={<AdminTenants />} />
                {/* 2026-10-14 (Itachi, "la définition des tarifs ... éparpillé
                    en pages et onglets") — /admin/commerce (AdminCommerceConfig,
                    qui ne faisait que wrapper AdminDocuments) et toutes les
                    anciennes routes de tarification redirigent maintenant vers
                    la page unique AdminPricing.jsx. AdminCommerceConfig.jsx
                    reste en place (inerte, cet environnement ne permet pas de
                    supprimer un fichier) mais n'est plus jamais monté. */}
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="commerce" element={<Navigate to="/admin/pricing" replace />} />
                <Route path="pricing-plans" element={<Navigate to="/admin/pricing" replace />} />
                <Route path="documents" element={<Navigate to="/admin/pricing" replace />} />
                <Route path="tenants/:id" element={<AdminTenantDetail />} />
                <Route path="tenants/:id/billing" element={<AdminTenantBilling />} />
                <Route path="health" element={<AdminHealth />} />
                {/* Sprint 16.1 (Bloc 3, continued) — Verticales/Personas IA/
                    Playbooks IA merged into tabs on one page, per Itachi's
                    2026-07-25 direction (genuinely different data models,
                    but configured together — see AdminVerticalConfig.jsx's
                    own comment). verticals/:id stays its own separate
                    route — a drill-down, not a sibling config surface. */}
                <Route path="verticals" element={<AdminVerticalConfig />} />
                <Route path="verticals/:id" element={<AdminVerticalDetail />} />
                <Route path="roles" element={<AdminRoles />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="billing" element={<AdminBilling />} />
                {/* 2026-10-14 — voice-pricing's DEFINITION moved to AdminPricing
                    (/admin/pricing, "Voix" section); "revenue" stays reporting,
                    still a tab on AdminBilling. */}
                <Route path="voice-pricing" element={<Navigate to="/admin/pricing" replace />} />
                <Route path="revenue" element={<Navigate to="/admin/billing?tab=revenue" replace />} />
                <Route path="legal" element={<AdminLegal />} />
                <Route path="playbooks" element={<Navigate to="/admin/verticals?tab=playbooks" replace />} />
                <Route path="vertical-personas" element={<Navigate to="/admin/verticals?tab=personas" replace />} />
                {/* Sprint 16.1 (Bloc 3, continued) — the legacy flow-script
                    editor is gone per Itachi's 2026-07-25 direction; only
                    the "bêta" V2 editor (which already superseded it —
                    same FlowGraphCanvas/FlowScriptTabs building blocks)
                    remains. Old bookmarks redirect here instead of 404ing. */}
                <Route path="flow-scripts" element={<Navigate to="/admin/flow-editor" replace />} />
                <Route path="flow-editor" element={<AdminFlowEditorV2 />} />
                <Route path="latency-dashboards" element={<AdminLatencyDashboards />} />
                {/* Chantier B1 (feuille de route 2026-08-24) — le backend H6
                    existait déjà (routes/admin_understanding_quality.py) sans
                    page pour le consulter. */}
                <Route path="understanding-quality" element={<AdminUnderstandingQuality />} />
                {/* 2026-09-28 — chantier observabilité (Itachi, priorité #1
                    après la session de diagnostic du 28/09) : taux de reask,
                    taux de transfert humain, taux d'abandon avant
                    finalisation, latence STT→LLM→TTS, par tenant. */}
                <Route path="voice-observability" element={<AdminVoiceObservability />} />
                <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
                <Route path="learning-lab" element={<AdminLearningLab />} />
                {/* Sprint 16.1 (Bloc 3) — merged into one tabbed page (same
                    pattern as pricing-plans/documents -> commerce above);
                    legacy routes redirect so old bookmarks work. */}
                <Route path="latency" element={<Navigate to="/admin/latency-dashboards?tab=m3" replace />} />
                <Route path="flow-latency" element={<Navigate to="/admin/latency-dashboards?tab=flow" replace />} />
                <Route path="settings" element={<AdminSettings />} />
                {/* Sprint 16.1 (Bloc 3) — renamed from onboarding-settings
                    to resolve the naming collision with tenant-facing
                    onboarding (see AdminTrialSettings.jsx's own comment). */}
                <Route path="trial-settings" element={<AdminTrialSettings />} />
                <Route path="onboarding-settings" element={<Navigate to="/admin/trial-settings" replace />} />
                <Route path="audit" element={<AuditLog globalScope />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
